import { App } from '@capacitor/app';
import { useTranslate } from '@tolgee/react';
import { useAuth } from 'lincd-auth/hooks/useAuth';
import { Server } from 'lincd-server-utils/utils/Server';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTorchCounter } from '../../../hooks/useTorchCounter.js';
import { useTorchPresence } from '../../../hooks/useTorchPresence.js';
import { packageName } from '../../../package.js';
import { ROUTES } from '../../../routes.js';
import { isCleanName } from '../../../utils/name-validation.js';
import {
  clearPeaceFlameRegisterPending,
  getPeaceFlameSourceUrl,
  setPeaceFlameRegisterPending,
} from '../../../utils/peaceflame-register.js';
import { BefriendDrawer } from './BefriendDrawer.js';
import PeaceFlameErrorBoundary from './ErrorBoundary.js';
import { PeaceFlame } from './PeaceFlame.js';
import style from './TorchOverlay.module.css';
import { TorchPresence } from './TorchPresence.js';
import { TorchSvg } from './TorchSvg.js';

export interface TorchOverlayProps {
  onClose: () => void;
  /** Strips user-context strings; reserved for future pre-auth copy variations. */
  isPreAuth?: boolean;
}

type Phase = 'idle' | 'lit' | 'zoomed';
type PermissionState = 'unknown' | 'granted' | 'denied';
type RelayBearer = {
  name?: string;
  city?: string;
  country?: string;
};

const ZOOM_IN = 1.5;
/**
 * Hold the canvas at zoom=1 well after the incoming-torch animation completes
 * (~3500ms) and the user's flame ignites (3000ms). The user wants a clear
 * "lit moment" to land before the camera moves; PeaceFlame's lerp then takes
 * ~1.5s to slowly grow into the zoom, finishing around 6500ms.
 */
const PRE_ZOOM_HOLD_MS = 5000;
/**
 * Total duration of the receive moment — text overlay + incoming-torch
 * animation (~3.5s) + small post-ignition hold so the message lands. Plan
 * 001 Phase 1.1 deferred polish extended this from 2.5s for a more
 * contemplative arrival.
 */
const RECEIVE_OVERLAY_MS = 12_000;
/** Duration of the incoming-torch CSS animation. Mirrors the keyframe
 *  duration in TorchOverlay.module.css. Used to time the JSX unmount so the
 *  element doesn't linger as an invisible div after the keyframes finish. */
const INCOMING_TORCH_MS = 3500;
/**
 * When (in ms relative to beginReveal) the user's flame catches fire from
 * the incoming torch. Slightly before the incoming torch fully merges
 * (at 100% of 3500ms) so the user's flame begins puffing up as the giving
 * torch dissolves into it.
 */
const FLAME_IGNITION_MS = 3000;
const PASS_OVERLAY_MS = 6000;
/**
 * Plan 003 Stage-3: localStorage key + min-interval (ms) between account-prompt
 * appearances. After a dismissal we don't re-prompt for this long.
 *
 * Production: 24h — a returning user the next day sees it again, but a
 * same-session close-and-reopen doesn't nag.
 *
 * Development: 30s — short enough to iterate on the UX without manually
 * clearing localStorage between tests. To force the prompt at any time, run
 * `localStorage.removeItem('pf_account_prompt_dismissed_at')` in devtools.
 */
const ACCOUNT_PROMPT_DISMISSED_KEY = 'pf_account_prompt_dismissed_at';
const ACCOUNT_PROMPT_COOLDOWN_MS =
  process.env.NODE_ENV === 'production' ? 24 * 60 * 60 * 1000 : 30 * 1000;
/**
 * Min ms between in-session pass-overlay triggers from presence events.
 * 15s in dev (you can simulate 1-2 cross-client lights and see the
 * behavior without spam). Production target: 60s+ to keep the moment
 * meaningful at concert scale where many bearers are lighting per second.
 */
const PASS_EVENT_THROTTLE_MS =
  process.env.NODE_ENV === 'production' ? 60_000 : 15_000;
/**
 * Delay after the flame is lit before the BefriendSheet first appears, and
 * delay after each dismissal before the next auto-open. Per plan 003 Phase 6
 * the contemplative moment of holding the torch should land before the prompt.
 */
const BEFRIEND_SHEET_DELAY_MS = 30_000;

interface IosDOE {
  requestPermission?: () => Promise<'granted' | 'denied'>;
}

/**
 * Plan 003 Stage-3: check whether the account prompt has been dismissed
 * recently. Reads localStorage; returns true if we should suppress the
 * prompt for now. Wrapped in try/catch because localStorage can throw in
 * private-mode / disabled-storage browsers — fall through to "not
 * dismissed" so the prompt can still appear.
 */
function accountPromptRecentlyDismissed(): boolean {
  try {
    const raw = window.localStorage.getItem(ACCOUNT_PROMPT_DISMISSED_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < ACCOUNT_PROMPT_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markAccountPromptDismissed(): void {
  try {
    window.localStorage.setItem(
      ACCOUNT_PROMPT_DISMISSED_KEY,
      String(Date.now())
    );
  } catch {
    /* private-mode: silently no-op; user will see the prompt next time */
  }
}

async function requestTiltPermission(): Promise<PermissionState> {
  if (typeof window === 'undefined') return 'granted';
  const DOE = (window as unknown as { DeviceOrientationEvent?: IosDOE })
    .DeviceOrientationEvent;
  if (DOE && typeof DOE.requestPermission === 'function') {
    try {
      const result = await DOE.requestPermission();
      return result === 'granted' ? 'granted' : 'denied';
    } catch {
      return 'denied';
    }
  }
  // Non-iOS: no permission required; assume the listener will simply work
  // (or silently no-op on platforms without the deviceorientation event).
  return 'granted';
}

async function requestMicPermission(): Promise<PermissionState> {
  if (typeof window === 'undefined' || !navigator?.mediaDevices?.getUserMedia) {
    return 'denied';
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // We don't keep this stream — PeaceFlame will request its own when
    // audioSource flips to 'mic'. Stop the tracks immediately.
    stream.getTracks().forEach((t) => t.stop());
    return 'granted';
  } catch {
    return 'denied';
  }
}

/**
 * Fullscreen modal shell hosting the live PeaceFlame.
 *
 * Mounted only while open (parent controls via conditional render). On
 * mount: shows the whole torch in `idle` with a pre-prompt explainer card.
 * After the user taps Continue (or Just-light-it), the state moves through
 * `lit` → `zoomed`, applying a CSS scale transform on the canvas wrapper to
 * bring the flame to center-stage.
 *
 * Close is instant — clicking ×, pressing Escape, or hardware back calls
 * onClose() which unmounts this component immediately. No delayed exit
 * animation; the visual disappears the moment the user asks for it.
 *
 * Body scroll is locked while mounted. Permissions degrade gracefully:
 * silent mode if mic is denied; static indigo if tilt is denied. Post-denial
 * [Enable music] / [Enable tilt] buttons remain available.
 */
export function TorchOverlay({ onClose, isPreAuth }: TorchOverlayProps) {
  const { t } = useTranslate();
  const auth = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('idle');
  const [micState, setMicState] = useState<PermissionState>('unknown');
  const [tiltState, setTiltState] = useState<PermissionState>('unknown');
  // Plan 003 Stage-3: account-as-bridge prompt shown on close for anonymous
  // users who haven't dismissed recently. Now superseded for anon users with
  // a successfully-lit session by the richer relay-story card (spec Part 6
  // item T); kept as the fallback path for sessions where the relay story
  // isn't meaningful (e.g. anon user never reached ignition).
  const [showAccountPrompt, setShowAccountPrompt] = useState(false);
  // Spec Part 6 item T: post-event relay story artifact card. Shown on
  // close for anonymous users who actually lit the flame, replacing the
  // simpler account prompt with a personal narrative + share + claim CTA.
  const [showRelayStory, setShowRelayStory] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);
  // Mock travel counts for the relay narrative. Real values arrive once
  // plan 003 Phase 3 (torch chain) lands and we can query "lights where
  // receivedFrom = me" + distinct bearer countries. For now generate
  // plausible numbers from the user's bearer number so the same session
  // shows the same values across re-renders.
  const [travelPeople, setTravelPeople] = useState<number>(0);
  const [travelCountries, setTravelCountries] = useState<number>(0);

  // Phase 9 — receive + pass scripted overlays.
  const [showReceive, setShowReceive] = useState(false);
  // Plan 001 Phase 1.1 polish — incoming torch animation tied to receive.
  const [showIncomingTorch, setShowIncomingTorch] = useState(false);
  /** False before the incoming torch "kisses" the wick (no particles); flips
   *  true at FLAME_IGNITION_MS, triggering PeaceFlame's puff-up boost. */
  const [flameLit, setFlameLit] = useState(false);
  const [bearerNumber, setBearerNumber] = useState<number | null>(null);
  const [currentLightId, setCurrentLightId] = useState<string | null>(null);
  const [receivedFrom, setReceivedFrom] = useState<RelayBearer | null>(null);
  const [passTarget, setPassTarget] = useState<RelayBearer | null>(null);
  const [queuedPassTarget, setQueuedPassTarget] = useState<{
    bearer?: RelayBearer;
  } | null>(null);
  const [showPass, setShowPass] = useState(false);
  const [closingWithPass, setClosingWithPass] = useState(false);
  const closeTimerRef = useRef<number | null>(null);
  /** Auto-hide timer for the receive overlay; reset when relay metadata arrives. */
  const receiveHideTimerRef = useRef<number | null>(null);
  /** Tracks the most recent pass-overlay trigger time for the throttle. */
  const lastPassFiredAtRef = useRef<number>(0);
  /** Auto-hide timer for in-session pass overlays (not the close-flow timer). */
  const passHideTimerRef = useRef<number | null>(null);

  // Plan 003 Stage-2 (named-but-no-account): first-name capture on the
  // pre-prompt screen. Optional. If non-empty when Continue/Just-light-it is
  // tapped, we fire-and-forget setGuestName to attach it to the guest Player.
  const [firstName, setFirstName] = useState('');

  // Plan 003 Phase 6 — Befriend bottom sheet.
  // Sheet is always mounted; toggles between collapsed (peek showing handle +
  // "Befriending" title) and expanded (full prompt + CTA).
  // 30s after the flame is lit, auto-expand once for the initial prompt; after
  // that the user controls expansion via the handle.
  const [befriendExpanded, setBefriendExpanded] = useState(false);
  /** True until the sheet has been expanded once in this torch session;
   *  drives the 2.5s breathing beat on first expand only. */
  const befriendFirstExpandRef = useRef(true);
  /** Timer that fires the initial auto-expand 30s post-light. */
  const befriendAutoExpandTimerRef = useRef<number | null>(null);

  // Live torches-lit counter + per-event presence pulses. The presence
  // hook's onEvent callback syncs the counter to the event's `count` so
  // cross-client number updates appear within the 10s presence-poll window
  // (instead of waiting for the 60s useAppConfig revalidate). Specific
  // `torch:passed` events drive the pass overlay only when the next bearer
  // received from this user's current light.
  const { count: worldCount, bumpOptimistic, syncTo } = useTorchCounter();
  const { events: presenceEvents } = useTorchPresence({
    active: phase !== 'idle',
    onEvent: (event) => {
      if (event.count > 0) syncTo(event.count);
      if (event.type === 'torch:passed') {
        maybeFireInSessionPass(event);
      }
    },
  });

  const firePassOverlay = (target?: RelayBearer) => {
    if (closingWithPass) return; // close flow takes precedence
    if (showPass) return; // already animating — only one at a time
    const now = Date.now();
    if (now - lastPassFiredAtRef.current < PASS_EVENT_THROTTLE_MS) return;
    lastPassFiredAtRef.current = now;
    setPassTarget(target ?? null);
    setShowPass(true);
    if (passHideTimerRef.current !== null) {
      window.clearTimeout(passHideTimerRef.current);
    }
    passHideTimerRef.current = window.setTimeout(() => {
      passHideTimerRef.current = null;
      setShowPass(false);
    }, PASS_OVERLAY_MS);
  };

  const maybeFireInSessionPass = (event: {
    receivedFromLightId?: string;
    bearer?: RelayBearer;
  }) => {
    if (!currentLightId || event.receivedFromLightId !== currentLightId) {
      return;
    }
    // Per user feedback: never fire "your flame is being passed" while the
    // user's own flame is still being lit. Queue it until the entire
    // receive sequence is done — incoming torch dissolved, receive text
    // gone, flame fully ignited.
    if (!flameLit || showReceive || showIncomingTorch) {
      setQueuedPassTarget({ bearer: event.bearer });
      return;
    }
    firePassOverlay(event.bearer);
  };

  useEffect(() => {
    if (!queuedPassTarget) return;
    if (!flameLit || showReceive || showIncomingTorch) return;
    const target = queuedPassTarget.bearer;
    setQueuedPassTarget(null);
    firePassOverlay(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queuedPassTarget, flameLit, showReceive, showIncomingTorch]);

  // Focus management: capture the element that had focus before the modal
  // opened so we can restore focus to it on close.
  const prevFocusRef = useRef<HTMLElement | null>(null);
  const continueBtnRef = useRef<HTMLButtonElement | null>(null);

  // The overlay root element. Held in state (not a plain ref) so the
  // BefriendDrawer re-renders once it's available and portals into this
  // fullscreen stacking context instead of document.body (which sits behind
  // the overlay's high z-index and would hide the drawer's peek).
  const [overlayEl, setOverlayEl] = useState<HTMLDivElement | null>(null);

  // Body scroll lock + focus management for the lifetime of the modal.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    prevFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    // Move focus into the modal so screen readers + keyboard nav land here.
    // Defer to a microtask so the Continue button is mounted.
    queueMicrotask(() => {
      continueBtnRef.current?.focus();
    });
    return () => {
      document.body.style.overflow = prev;
      // Restore focus to wherever it was before the modal opened.
      prevFocusRef.current?.focus();
    };
  }, []);

  // Escape key + Capacitor hardware back — both route through the pass-
  // overlay flow (handleCloseWithPass) so the user sees "Your flame is
  // traveling to another bearer..." before the modal unmounts.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCloseWithPass();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    let removeListener: (() => void) | undefined;
    (async () => {
      try {
        const handle = await App.addListener('backButton', () =>
          handleCloseWithPass()
        );
        if (cancelled) {
          handle.remove();
          return;
        }
        removeListener = () => handle.remove();
      } catch {
        // App.addListener may be unavailable in pure-web context — silently skip.
      }
    })();
    return () => {
      cancelled = true;
      if (removeListener) removeListener();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBefriendExpand = () => {
    setBefriendExpanded(true);
  };

  const handleBefriendCollapse = () => {
    setBefriendExpanded(false);
    befriendFirstExpandRef.current = false; // subsequent expands skip the breathing beat
    // Cancel any pending auto-expand since the user has now seen the sheet.
    if (befriendAutoExpandTimerRef.current !== null) {
      window.clearTimeout(befriendAutoExpandTimerRef.current);
      befriendAutoExpandTimerRef.current = null;
    }
  };

  const scheduleInitialAutoExpand = () => {
    if (befriendAutoExpandTimerRef.current !== null) {
      window.clearTimeout(befriendAutoExpandTimerRef.current);
    }
    befriendAutoExpandTimerRef.current = window.setTimeout(() => {
      befriendAutoExpandTimerRef.current = null;
      setBefriendExpanded(true);
    }, BEFRIEND_SHEET_DELAY_MS);
  };

  const scheduleReceiveHide = () => {
    if (receiveHideTimerRef.current !== null) {
      window.clearTimeout(receiveHideTimerRef.current);
    }
    receiveHideTimerRef.current = window.setTimeout(() => {
      receiveHideTimerRef.current = null;
      setShowReceive(false);
    }, RECEIVE_OVERLAY_MS);
  };

  const beginReveal = () => {
    // idle → lit (immediate mount of PeaceFlame at zoom=1 so the whole torch
    // is visible) → hold for PRE_ZOOM_HOLD_MS → zoomed (CSS transition kicks
    // in to scale the canvas wrapper).
    setPhase('lit');
    setCurrentLightId(null);
    setReceivedFrom(null);
    setPassTarget(null);
    setQueuedPassTarget(null);
    window.setTimeout(() => setPhase('zoomed'), PRE_ZOOM_HOLD_MS);

    // Schedule the Befriend sheet's first auto-expand (peek is already
    // visible from the moment the sheet mounts — only the expansion is timed).
    scheduleInitialAutoExpand();

    // Receive overlay shows immediately. The bearer number and relay metadata
    // fade in once incrementTorchesLit's response arrives. The hide timer is
    // reset when metadata arrives so the location has a full read window.
    setShowReceive(true);
    setShowIncomingTorch(true);
    // Flame stays unlit until the incoming torch reaches the wick.
    setFlameLit(false);
    window.setTimeout(() => setFlameLit(true), FLAME_IGNITION_MS);
    scheduleReceiveHide();
    window.setTimeout(() => setShowIncomingTorch(false), INCOMING_TORCH_MS);

    // INSTANT optimistic bump — fires synchronously so the user sees +1 the
    // moment they commit. Will be settled to the actual value via syncTo
    // when the increment response arrives.
    bumpOptimistic();

    // Response carries the authoritative count (= user's bearer number for
    // this session) plus relay metadata when available. syncTo updates the
    // counter to that value (handles dedupe + multi-client accuracy in one
    // call; also clears the optimistic offset).
    Server.call(packageName, 'incrementTorchesLit')
      .then(
        (
          result:
            | {
                count?: number;
                lightId?: string;
                receivedFrom?: RelayBearer;
              }
            | undefined
        ) => {
          if (result && typeof result.count === 'number') {
            setBearerNumber(result.count);
            if (typeof result.lightId === 'string') {
              setCurrentLightId(result.lightId);
            }
            setReceivedFrom(result.receivedFrom ?? null);
            if (result.receivedFrom) {
              scheduleReceiveHide();
            }
            syncTo(result.count);
            // Generate stable mock travel counts seeded by the bearer number
            // so the same session always shows the same story (matters when
            // the close-flow card re-renders). Real numbers land with plan
            // 003 Phase 3's torch chain.
            setTravelPeople((result.count % 23) + 3); // 3..25
            setTravelCountries((result.count % 6) + 2); // 2..7
          }
        }
      )
      .catch((err) =>
        console.warn('incrementTorchesLit failed (non-fatal):', err)
      );
  };

  /**
   * Close-with-pass: show the pass overlay for 2s, then either show the
   * Stage-3 account prompt (if anonymous + not recently dismissed) or
   * unmount. Guarded against double-fire.
   */
  const handleCloseWithPass = () => {
    if (closingWithPass) return;
    setClosingWithPass(true);
    setPassTarget(null);
    setShowPass(true);
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      // Plan 003 Stage-3: on close, anonymous users see a story-driven card
      // (spec Part 6 item T) that combines artifact + share + account CTA.
      // The simpler text-only account prompt is the fallback when the
      // relay story isn't meaningful (e.g. user never reached ignition).
      const isAnonymous = !auth?.userAccount;
      const recentlyDismissed = accountPromptRecentlyDismissed();
      if (isAnonymous && !recentlyDismissed) {
        setShowPass(false);
        if (flameLit && bearerNumber !== null) {
          setShowRelayStory(true);
        } else {
          setShowAccountPrompt(true);
        }
        return;
      }
      console.log(
        `[close-prompt] skipped: ${
          !isAnonymous ? 'user signed in' : 'cooldown active'
        }`
      );
      onClose();
    }, PASS_OVERLAY_MS);
  };

  // Social-share handler was here; removed pending future iteration. The
  // bearer/travel data is still computed and passed into the card so we can
  // wire native share back in later without re-plumbing.

  const continueToRegister = async () => {
    if (creatingAccount) return;
    setCreatingAccount(true);

    try {
      const trimmedFirstName = firstName.trim();
      if (trimmedFirstName && isCleanName(trimmedFirstName)) {
        setPeaceFlameRegisterPending(trimmedFirstName);
      } else {
        setPeaceFlameRegisterPending();
      }
      if (!auth?.userAccount) {
        const response = await Server.call(
          packageName,
          'signinTemporaryForPeaceFlame',
          firstName
        );
        if (!response?.auth) {
          throw new Error(response?.error || 'temporary_signin_failed');
        }
        auth.updateAuth({
          auth: response.auth,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
        });
      }
      onClose();
      navigate(getPeaceFlameSourceUrl(ROUTES.register.path));
    } catch (err) {
      clearPeaceFlameRegisterPending();
      console.error('PeaceFlame temporary signin failed:', err);
      setCreatingAccount(false);
    }
  };

  const handleRelayStorySave = () => {
    // "Save Forever" = create an account to claim the artifact permanently.
    // Don't mark dismissed — they engaged. If they bail out of /register
    // we want to re-prompt on a future close (after cooldown).
    void continueToRegister();
  };

  const handleRelayStoryDismiss = () => {
    markAccountPromptDismissed();
    setShowRelayStory(false);
    onClose();
  };

  const handleAccountPromptDismiss = () => {
    markAccountPromptDismissed();
    setShowAccountPrompt(false);
    onClose();
  };

  const handleAccountPromptAccept = () => {
    // Don't mark as dismissed — they engaged. If they bail out of /register
    // we still want to nudge again on a future close (after the cooldown).
    void continueToRegister();
  };

  const handleSignIn = () => {
    setPeaceFlameRegisterPending();
    onClose();
    navigate(getPeaceFlameSourceUrl(ROUTES.signin.path));
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      if (passHideTimerRef.current !== null) {
        window.clearTimeout(passHideTimerRef.current);
        passHideTimerRef.current = null;
      }
      if (receiveHideTimerRef.current !== null) {
        window.clearTimeout(receiveHideTimerRef.current);
        receiveHideTimerRef.current = null;
      }
      if (befriendAutoExpandTimerRef.current !== null) {
        window.clearTimeout(befriendAutoExpandTimerRef.current);
        befriendAutoExpandTimerRef.current = null;
      }
    };
  }, []);

  /**
   * Plan 003 Stage-2: fire-and-forget capture of the user's first name.
   * Optional — empty string is a no-op. Profanity filter intentionally
   * SILENT here: if the name is flagged we just don't send it (the user
   * proceeds as anonymous). No inline error, no warm-red border. Visible
   * feedback would tell abusers their attempt was detected and invites
   * workaround attempts; silent failure removes the cat-and-mouse incentive
   * for a casual, low-stakes input field. Per user direction: registration
   * (which is higher-stakes) DOES surface the rejection so real users can
   * fix their input.
   *
   * Backend method is currently a stub (see Phase 4 in plan 003).
   */
  const captureFirstNameIfProvided = () => {
    const trimmed = firstName.trim();
    if (!trimmed) return;
    if (!isCleanName(trimmed)) return; // silently treat as anonymous
    Server.call(packageName, 'setGuestName', trimmed).catch((err) =>
      console.warn('setGuestName failed (non-fatal):', err)
    );
  };

  const handleContinue = async () => {
    captureFirstNameIfProvided();
    // Both permissions fire from a single user gesture so iOS counts them.
    const [mic, tilt] = await Promise.all([
      requestMicPermission(),
      requestTiltPermission(),
    ]);
    setMicState(mic);
    setTiltState(tilt);
    beginReveal();
  };

  const handleSilent = () => {
    captureFirstNameIfProvided();
    setMicState('denied');
    setTiltState('denied');
    beginReveal();
  };

  const handleEnableMic = async () => {
    const next = await requestMicPermission();
    setMicState(next);
  };

  const handleEnableTilt = async () => {
    const next = await requestTiltPermission();
    setTiltState(next);
  };

  const showPeaceFlame = phase !== 'idle';
  const currentZoom = phase === 'zoomed' ? ZOOM_IN : 1;
  const audioSource: 'mic' | 'silent' =
    micState === 'granted' ? 'mic' : 'silent';
  const tiltProp: 'auto' | 'off' = tiltState === 'granted' ? 'auto' : 'off';
  const formatRelayBearer = (bearer: RelayBearer | null): string | null => {
    if (!bearer) return null;
    const place = [bearer.city, bearer.country].filter(Boolean).join(', ');
    if (bearer.name && place) return `${bearer.name} in ${place}`;
    if (bearer.name) return bearer.name;
    if (place) return `a bearer in ${place}`;
    return null;
  };
  const receivedFromLabel = formatRelayBearer(receivedFrom);
  const passTargetLabel = formatRelayBearer(passTarget);

  return (
    <div
      ref={setOverlayEl}
      className={style.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={t('peaceFlame.title', 'Torch of Peace')}
    >
      {showPeaceFlame && (
        <div className={style.canvasLayer}>
          <PeaceFlameErrorBoundary>
            <PeaceFlame
              audioSource={audioSource}
              tilt={tiltProp}
              tiltState={tiltState}
              zoom={currentZoom}
              paused={false}
              lit={flameLit}
              ariaLabel={t(
                'peaceFlame.canvasAria',
                'Audio-reactive flame of peace'
              )}
              onMicPermission={(s) => setMicState(s)}
              onTiltPermission={(s) => setTiltState(s)}
            />
          </PeaceFlameErrorBoundary>
          {showIncomingTorch && (
            <>
              <div className={style.incomingTorch} aria-hidden="true">
                <TorchSvg />
              </div>
              <div className={style.ignitionFlare} aria-hidden="true" />
            </>
          )}
        </div>
      )}

      {phase !== 'idle' && (
        <TorchPresence events={presenceEvents} worldCount={worldCount} />
      )}

      {showReceive && (
        <div className={style.receiveOverlay} role="status" aria-live="polite">
          <p className={style.receiveLine1}>
            {receivedFromLabel
              ? t(
                  'peaceFlame.receiveMsgWithBearer',
                  'The torch is traveling to you from {bearer}.',
                  {
                    bearer: receivedFromLabel,
                  }
                )
              : t(
                  'peaceFlame.receiveMsg',
                  'The torch is traveling to you from a peace bearer somewhere in the world.'
                )}
          </p>
          {bearerNumber !== null && (
            <p className={style.receiveLine2}>
              {t('peaceFlame.bearerNumber', 'You are torch bearer #{number}', {
                number: bearerNumber,
              })}
            </p>
          )}
        </div>
      )}

      {showPass && (
        <div className={style.passOverlay} role="status" aria-live="polite">
          <p className={style.passLine}>
            {passTargetLabel
              ? t(
                  'peaceFlame.passMsgWithBearer',
                  'Your flame is traveling to {bearer}.',
                  {
                    bearer: passTargetLabel,
                  }
                )
              : t(
                  'peaceFlame.passMsg',
                  'Your flame is traveling to another bearer somewhere in the world.'
                )}
          </p>
        </div>
      )}

      <div className={style.chrome}>
        <div className={style.topRow}>
          <button
            type="button"
            className={style.closeButton}
            onClick={handleCloseWithPass}
            aria-label={t(
              'peaceFlame.modalDismissAria',
              'Close Torch of Peace'
            )}
          >
            ×
          </button>
        </div>

        {phase === 'idle' && (
          <div className={style.prePrompt}>
            <h2 className={style.prePromptTitle}>
              {t('peaceFlame.prePromptTitle', 'Ready to light the flame?')}
            </h2>
            <p className={style.prePromptBody}>
              {t(
                'peaceFlame.prePromptBody',
                "To make the flame dance with the music and respond when you tilt your phone, we'll ask for microphone and motion access."
              )}
            </p>
            <label className={style.nameField}>
              <span className={style.nameLabel}>
                {t(
                  'peaceFlame.namePromptLabel',
                  'Introduce yourself to torch bearers around the world.'
                )}
              </span>
              <input
                type="text"
                className={style.nameInput}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={t(
                  'peaceFlame.namePromptPlaceholder',
                  'First name'
                )}
                autoComplete="given-name"
                maxLength={60}
                aria-label={t(
                  'peaceFlame.namePromptAria',
                  'Your first name (optional)'
                )}
              />
            </label>
            <div className={style.prePromptButtons}>
              <button
                ref={continueBtnRef}
                type="button"
                className={style.continueBtn}
                onClick={handleContinue}
              >
                {t('peaceFlame.continueCta', 'Continue')}
              </button>
              <button
                type="button"
                className={style.silentBtn}
                onClick={handleSilent}
              >
                {t('peaceFlame.silentCta', 'Just light it')}
              </button>
            </div>
          </div>
        )}

        {phase !== 'idle' && (
          <div
            className={style.bottomRow}
            data-peaceflame-bottom-chrome
          >
            {micState !== 'granted' && (
              <button
                type="button"
                className={style.enableBtn}
                onClick={handleEnableMic}
              >
                {t('peaceFlame.enableMusic', 'Enable music')}
              </button>
            )}
            {tiltState !== 'granted' && (
              <button
                type="button"
                className={style.enableBtn}
                onClick={handleEnableTilt}
              >
                {t('peaceFlame.enableTilt', 'Enable tilt')}
              </button>
            )}
          </div>
        )}
      </div>

      {phase !== 'idle' && overlayEl && (
        <BefriendDrawer
          expanded={befriendExpanded}
          onExpand={handleBefriendExpand}
          onCollapse={handleBefriendCollapse}
          firstExpandThisSession={befriendFirstExpandRef.current}
          container={overlayEl}
        />
      )}

      {showAccountPrompt && (
        <>
          <div
            className={style.accountPromptBackdrop}
            onClick={handleAccountPromptDismiss}
            aria-hidden="true"
          />
          <div
            className={style.accountPromptCard}
            role="dialog"
            aria-modal="true"
            aria-labelledby="peaceFlameAccountPromptTitle"
          >
            <h2
              id="peaceFlameAccountPromptTitle"
              className={style.accountPromptTitle}
            >
              {t(
                'peaceFlame.accountPromptTitle',
                'Continue your peace practice?'
              )}
            </h2>
            <p className={style.accountPromptBody}>
              {t(
                'peaceFlame.accountPromptBody',
                'Create an account to keep your story and join the Peace Game community.'
              )}
            </p>
            <button
              type="button"
              className={style.accountPromptCta}
              onClick={handleAccountPromptAccept}
              disabled={creatingAccount}
              autoFocus
            >
              {t('peaceFlame.accountPromptCta', 'Create Account')}
            </button>
            <button
              type="button"
              className={style.accountPromptSignin}
              onClick={handleSignIn}
              disabled={creatingAccount}
            >
              {t('peaceFlame.accountPromptSignin', 'Sign in')}
            </button>
            <button
              type="button"
              className={style.accountPromptDismiss}
              onClick={handleAccountPromptDismiss}
              disabled={creatingAccount}
            >
              {t('peaceFlame.accountPromptDismiss', 'Not now')}
            </button>
          </div>
        </>
      )}

      {showRelayStory && (
        <>
          <div
            className={style.relayStoryBackdrop}
            onClick={handleRelayStoryDismiss}
            aria-hidden="true"
          />
          <div
            className={style.relayStoryCard}
            role="dialog"
            aria-modal="true"
            aria-labelledby="peaceFlameRelayStoryTitle"
          >
            <div className={style.relayStoryIcon} aria-hidden="true">
              <TorchSvg />
            </div>
            <p className={style.relayStoryEyebrow}>
              {t(
                'peaceFlame.relayEyebrow',
                'Thank you for passing the Peace Flame!'
              )}
            </p>
            <h2
              id="peaceFlameRelayStoryTitle"
              className={style.relayStoryTitle}
            >
              {t('peaceFlame.relayBearerLine', 'Torch Bearer #{bearer}', {
                bearer: (bearerNumber ?? 0).toLocaleString(),
              })}
            </h2>
            <p className={style.relayStoryBody}>
              {t(
                'peaceFlame.relayTravelLine',
                'Your Peace Flame lit the torch of {people} people across {countries} countries.',
                {
                  people: travelPeople,
                  countries: travelCountries,
                }
              )}
            </p>
            <button
              type="button"
              className={style.relayStorySave}
              onClick={handleRelayStorySave}
              disabled={creatingAccount}
              autoFocus
            >
              {t('peaceFlame.relaySaveCta', 'Create Account')}
            </button>
            <button
              type="button"
              className={style.relayStorySignin}
              onClick={handleSignIn}
              disabled={creatingAccount}
            >
              {t('peaceFlame.relaySigninCta', 'Sign in')}
            </button>
            <button
              type="button"
              className={style.relayStoryDismiss}
              onClick={handleRelayStoryDismiss}
              disabled={creatingAccount}
            >
              {t('peaceFlame.relayDismissCta', 'Maybe later')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
